import { useEffect, useRef, useState, useCallback } from 'react';

interface Dimensions {
    width: number;
    height: number;
}

interface UseChartDimensionsResult {
    /** Ref to attach to the container element */
    containerRef: React.RefObject<HTMLDivElement | null>;
    /** Current dimensions of the container */
    dimensions: Dimensions;
}

/**
 * Hook for tracking container dimensions for responsive chart sizing.
 * Uses ResizeObserver for efficient dimension tracking and handles
 * mobile orientation changes.
 *
 * @returns Object containing containerRef to attach to the element and current dimensions
 */
export function useChartDimensions(): UseChartDimensionsResult {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [dimensions, setDimensions] = useState<Dimensions>({ width: 0, height: 0 });

    const updateDimensions = useCallback(() => {
        if (containerRef.current) {
            const { width, height } = containerRef.current.getBoundingClientRect();
            setDimensions({ width, height });
        }
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Initial measurement
        updateDimensions();

        // Set up ResizeObserver for efficient resize tracking
        const resizeObserver = new ResizeObserver((entries) => {
            // Use requestAnimationFrame to avoid resize loop issues
            window.requestAnimationFrame(() => {
                if (entries.length > 0) {
                    const entry = entries[0];
                    if (entry) {
                        const { width, height } = entry.contentRect;
                        setDimensions({ width, height });
                    }
                }
            });
        });

        resizeObserver.observe(container);

        // Handle orientation change for mobile devices
        const handleOrientationChange = () => {
            // Small delay to allow the browser to complete orientation change
            setTimeout(updateDimensions, 100);
        };

        window.addEventListener('orientationchange', handleOrientationChange);

        // Cleanup
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, [updateDimensions]);

    return { containerRef, dimensions };
}
